// src/components/common/GoogleMapPreview.js
//
// Shared interactive map preview — replaces the old Google Static Maps
// <img> previews (Add Address modal, saved-address cards, Current
// Location cards) with a live @vis.gl/react-google-maps <Map>.
//
// Accepts either:
//   - addr.lat / addr.lng (already-known coordinates, e.g. from
//     navigator.geolocation), used directly, or
//   - address text (addr.formatted_address, or line1/area/city/state/
//     pincode) — geocoded client-side via the Geocoding API and cached
//     by address string so the same address isn't re-geocoded on every
//     render/remount.
//
// Renders nothing but the caller's empty-state UI when there's no API
// key, no address, or geocoding fails — same graceful-degradation
// contract the static <img onError={hide}> previews had.

import React, { useEffect, useState } from "react";
// Renamed on import — this file also uses the built-in JS `Map` collection
// (geocodeCache below); importing the library's Map component as `Map` would
// shadow that global and break `new Map()`.
import { APIProvider, Map as GoogleMap, Marker, useMapsLibrary } from "@vis.gl/react-google-maps";
import CONFIG from "../../config";

const GMAPS_KEY = CONFIG?.GOOGLE_MAPS_API_KEY || "";

// address string -> { lat, lng } | null — shared across every instance
// so switching tabs/re-rendering doesn't re-hit the Geocoding API.
const geocodeCache = new Map();

const addressTextOf = (addr) =>
  addr?.formatted_address ||
  [addr?.line1, addr?.area, addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(", ");

// JS Geocoder (google.maps.Geocoder via useMapsLibrary), NOT the REST
// geocode/json endpoint — a plain fetch() to that REST endpoint fails with
// "API keys with referer restrictions cannot be used with this API." for
// browser (HTTP-referrer-restricted) keys, which is exactly the key this
// app uses (see CurrentLocationDetector.js, which hit and fixed the same
// problem for reverse-geocoding). Forward-geocoding needs the identical
// fix — this used to be a raw fetch() here and silently failed with
// "Couldn't locate this address on the map" for every address.
const geocodeAddressViaJsApi = (geocodingLib, text) => new Promise((resolve) => {
  if (geocodeCache.has(text)) { resolve(geocodeCache.get(text)); return; }
  const geocoder = new geocodingLib.Geocoder();
  geocoder.geocode({ address: text }, (results, status) => {
    const loc = status === "OK" ? results?.[0]?.geometry?.location : null;
    const result = loc ? { lat: typeof loc.lat === "function" ? loc.lat() : loc.lat, lng: typeof loc.lng === "function" ? loc.lng() : loc.lng } : null;
    geocodeCache.set(text, result);
    resolve(result);
  });
});

// Same address_components -> field-shape mapping CurrentLocationDetector.js
// uses, reused here so a dragged/clicked pin fills the same fields a GPS
// detection would.
const addressFieldsFromGeocoderResult = (result) => {
  const comp = (type) => result.address_components?.find((c) => c.types.includes(type))?.long_name || "";
  return {
    line1: comp("premise") || comp("sublocality_level_2") || comp("route") || "",
    area:  comp("sublocality_level_1") || comp("sublocality") || comp("neighborhood") || "",
    city:  comp("locality") || comp("administrative_area_level_2") || "",
    state: comp("administrative_area_level_1") || "",
    pincode: comp("postal_code") || "",
    formatted_address: result.formatted_address || "",
  };
};

const EmptyState = ({ icon = "🗺", title, hint }) => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
    justifyContent:"center", height:"100%", gap:8, color:"#94a3b8", padding:"16px" }}>
    <div style={{ fontSize:32 }}>{icon}</div>
    <div style={{ fontSize:13, textAlign:"center" }}>{title}</div>
    {hint && <div style={{ fontSize:11, textAlign:"center" }}>{hint}</div>}
  </div>
);

// Rendered as a CHILD of <APIProvider> (not inline in GoogleMapPreview
// itself) — same split CurrentLocationDetector.js uses, since
// useMapsLibrary() only resolves the Provider's context from inside its
// actual child tree, not from the component that renders the Provider.
// Handles the editable (drag-to-place / click-to-place) interaction and its
// own reverse-geocode, entirely decoupled from the parent's text-driven
// geocoding once the user has manually placed a pin at least once — see
// `manualMode` below.
const InteractiveMap = ({ coords, height, zoom, editable, onPinMoved }) => {
  const geocodingLib = useMapsLibrary("geocoding");
  const [localCoords, setLocalCoords] = useState(coords);
  const manualMode = React.useRef(false);

  // Follow the parent's (text-geocoded) coords UNTIL the user manually
  // drags/clicks a pin — after that, this component is the source of truth
  // for position so a stray text edit elsewhere in the form can't yank the
  // pin away from where the user just placed it.
  useEffect(() => {
    if (!manualMode.current) setLocalCoords(coords);
  }, [coords]);

  const applyManualPosition = (lat, lng) => {
    manualMode.current = true;
    setLocalCoords({ lat, lng });
    if (!geocodingLib) { onPinMoved?.({ lat, lng }, null); return; }
    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      const fields = status === "OK" && results?.[0] ? addressFieldsFromGeocoderResult(results[0]) : null;
      onPinMoved?.({ lat, lng }, fields);
    });
  };

  const handleDragEnd = (e) => {
    const pos = e.latLng;
    if (!pos) return;
    applyManualPosition(typeof pos.lat === "function" ? pos.lat() : pos.lat, typeof pos.lng === "function" ? pos.lng() : pos.lng);
  };

  const handleMapClick = (e) => {
    if (!editable) return;
    const pos = e.detail?.latLng;
    if (!pos) return;
    applyManualPosition(pos.lat, pos.lng);
  };

  if (!localCoords) return null;

  return (
    <div style={{ width: "100%", height: `${height}px` }}>
      <GoogleMap
        defaultCenter={localCoords}
        center={editable ? localCoords : undefined}
        defaultZoom={zoom}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        style={{ width: "100%", height: "100%" }}
        onClick={editable ? handleMapClick : undefined}
      >
        <Marker position={localCoords} draggable={editable} onDragEnd={editable ? handleDragEnd : undefined} />
      </GoogleMap>
      {editable && (
        <div style={{ fontSize: 11, color: "#6b7280", padding: "6px 2px 0", textAlign: "center" }}>
          📍 Drag the pin or tap the map to fine-tune the exact location
        </div>
      )}
    </div>
  );
};

// Also rendered as a CHILD of <APIProvider> — owns the address-text ->
// coordinates resolution (needs useMapsLibrary("geocoding") for the same
// referrer-restriction reason InteractiveMap's reverse-geocode does), then
// renders either a status placeholder or the actual InteractiveMap once
// coordinates are known.
const MapCore = ({ addr, height, zoom, emptyTitle, emptyHint, onResolved, editable, onPinMoved }) => {
  const geocodingLib = useMapsLibrary("geocoding");
  const hasCoords = typeof addr?.lat === "number" && typeof addr?.lng === "number";
  const addrText  = hasCoords ? "" : addressTextOf(addr);
  const addrKey   = hasCoords ? `${addr.lat},${addr.lng}` : addrText;

  const [coords, setCoords]   = useState(hasCoords ? { lat: addr.lat, lng: addr.lng } : null);
  const [status, setStatus]   = useState(hasCoords ? "ready" : (addrText ? "loading" : "empty"));

  useEffect(() => {
    if (hasCoords) { setCoords({ lat: addr.lat, lng: addr.lng }); setStatus("ready"); return; }
    if (!addrText) { setCoords(null); setStatus("empty"); onResolved?.(null); return; }
    if (!geocodingLib) return; // still loading the geocoding library — wait for it
    let cancelled = false;
    setStatus("loading");
    // Debounced — addrText changes on every keystroke while a form is being
    // typed into (UserDashboard's AddressModal live preview); wait for a
    // pause before spending a Geocoding API call on it.
    const timer = setTimeout(() => {
      geocodeAddressViaJsApi(geocodingLib, addrText).then(result => {
        if (cancelled) return;
        if (result) { setCoords(result); setStatus("ready"); }
        else        { setCoords(null);    setStatus("error"); onResolved?.(null); }
      });
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addrKey, hasCoords, geocodingLib]);

  // Separate effect (not folded into the one above) so it also fires for
  // the hasCoords branch, and after geocoding resolves — anywhere `coords`
  // actually changes.
  useEffect(() => {
    if (status === "ready" && coords) onResolved?.(coords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords, status]);

  if (status === "empty")   return <EmptyState title={emptyTitle} hint={emptyHint} />;
  if (status === "loading") return <EmptyState icon="⏳" title="Loading map…" />;
  if (status === "error" || !coords) return <EmptyState title="Couldn't locate this address on the map" hint={emptyHint} />;

  return <InteractiveMap coords={coords} height={height} zoom={zoom} editable={editable} onPinMoved={onPinMoved} />;
};

// addr: { lat?, lng?, formatted_address?, line1?, area?, city?, state?, pincode? }
// onResolved(coords | null): fired whenever the shown coordinates change —
// either addr.lat/lng directly, or the result of geocoding addr's text.
// Lets a caller (e.g. UserDashboard's AddressModal) capture "whatever
// location this preview is currently showing" to save alongside the address,
// without storing it back onto `addr` itself (which would freeze future
// text edits to the first-resolved coordinates — see hasCoords above).
// editable (default false, every existing caller unaffected): renders a
// draggable/clickable pin instead of a read-only marker; onPinMoved(coords,
// addressFields|null) fires after each manual placement, addressFields
// reverse-geocoded in the same shape CurrentLocationDetector produces.
const GoogleMapPreview = ({ addr, height = 200, zoom = 16, emptyTitle = "No map preview available", emptyHint = "", onResolved, editable = false, onPinMoved }) => {
  if (!GMAPS_KEY) {
    return <EmptyState title={emptyTitle} hint={emptyHint || "Add a Google Maps API key to .env to enable map previews."} />;
  }
  return (
    <APIProvider apiKey={GMAPS_KEY}>
      <MapCore
        addr={addr} height={height} zoom={zoom} emptyTitle={emptyTitle} emptyHint={emptyHint}
        onResolved={onResolved} editable={editable} onPinMoved={onPinMoved}
      />
    </APIProvider>
  );
};

export default GoogleMapPreview;
